<template>
  <div class="outbound-picker">
    <div class="outbound-picker__hd" @click="expanded = !expanded">
      <span class="outbound-picker__title cn">从出货记录中选择</span>
      <span class="outbound-picker__toggle">{{ expanded ? '▾' : '▸' }}</span>
    </div>

    <div v-if="expanded" class="outbound-picker__bd">
      <!-- (a) 로딩 -->
      <div v-if="loading" class="outbound-picker__loading">
        <a-spin size="small" />
      </div>

      <!-- (e) 호출 실패 -->
      <div v-else-if="errorState" class="outbound-picker__msg outbound-picker__msg--error">
        <span class="cn">{{ errorMessage }}</span>
        <a class="outbound-picker__retry cn" @click="fetchCustomers">重试</a>
      </div>

      <!-- (b) 미연동 -->
      <div v-else-if="!mapped" class="outbound-picker__msg cn">
        出货数据尚未关联，请联系平台运营
      </div>

      <!-- (c) 빈 목록 -->
      <div v-else-if="total === 0" class="outbound-picker__msg cn">
        暂无出货记录
      </div>

      <!-- 정상 / (d) 절단 -->
      <template v-else>
        <a-input
          v-model:value="search"
          class="outbound-picker__search cn"
          placeholder="搜索客户名称或编码"
          allow-clear
        />

        <div class="outbound-picker__rows">
          <div
            v-for="customer in filteredCustomers"
            :key="customer.customerCode"
            class="outbound-picker__row"
            :class="{ 'outbound-picker__row--off': customer.registered }"
            @click="selectRow(customer)"
          >
            <span class="outbound-picker__code mono">{{ customer.customerCode }}</span>
            <span class="outbound-picker__name cn">
              {{ customer.customerName }}
              <span class="outbound-picker__meta cn mono">
                最近出货 {{ formatDate(customer.lastDeliveryDate) }} · {{ customer.lineCount }} 行
              </span>
            </span>
            <span v-if="customer.registered" class="outbound-picker__tag cn">已登记</span>
          </div>
        </div>

        <div v-if="truncated" class="outbound-picker__ft cn">
          仅显示最近 500 家出货客户（共 {{ total }} 家），可搜索筛选
        </div>
        <div v-else class="outbound-picker__ft cn">
          仅显示本公司出货客户 · 共 {{ total }} 家
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getOutboundCustomers } from '@/apis/chinaCompany';
import { extractErrorCodeToken } from '@/utils/outboundPickerError';

const GENERIC_LOAD_ERROR = '出货记录加载失败，可直接输入公司名称';

export default {
  name: 'OutboundCustomerPicker',

  // @MX:NOTE: [AUTO] REQ-UNW-006 준용 — class를 props로 선언하지 않음(fallthrough attribute 차단 방지)
  emits: ['select'],

  data() {
    return {
      loading: true,
      errorState: false,
      errorMessage: '',
      mapped: true,
      customers: [],
      total: 0,
      truncated: false,
      // REQ-OP-030: 생성 모드 기본 펼침
      expanded: true,
      search: '',
    };
  },

  computed: {
    filteredCustomers() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.customers;
      return this.customers.filter(
        (c) => c.customerCode.toLowerCase().includes(q) || String(c.customerName).toLowerCase().includes(q),
      );
    },
  },

  created() {
    this.fetchCustomers();
  },

  methods: {
    async fetchCustomers() {
      this.loading = true;
      this.errorState = false;
      this.errorMessage = '';
      try {
        const res = await getOutboundCustomers();
        const body = res?.data || {};
        this.mapped = body.mapped !== false;
        this.customers = Array.isArray(body.customers) ? body.customers : [];
        this.total = body.total || 0;
        this.truncated = !!body.truncated;
      } catch (err) {
        // REQ-OP-033: HTTP 2xx 형상 외 전부(400·404 포함) + 네트워크/타임아웃 → (e) 강하
        this.errorState = true;
        this.errorMessage = this.buildErrorMessage(err);
      } finally {
        this.loading = false;
      }
    },

    buildErrorMessage(err) {
      const status = err?.response?.status;
      const rawMessage = err?.response?.data?.message || err?.message || '';
      const token = extractErrorCodeToken(rawMessage);
      if (token) {
        // 코드 토큰 검출 — 중문 안내 주 표시 + 서버 원문 병기 (판단 34)
        return `暂时无法加载出货记录：${rawMessage}`;
      }
      if (status === 403) {
        // 미검출 403 — 서버 원문 폴백
        return rawMessage || GENERIC_LOAD_ERROR;
      }
      return GENERIC_LOAD_ERROR;
    },

    selectRow(customer) {
      // REQ-OP-032: 已登记 행은 클릭 무동작
      if (customer.registered) return;
      this.$emit('select', {
        customerCode: customer.customerCode,
        customerName: customer.customerName,
      });
    },

    formatDate(iso) {
      if (!iso) return '';
      return String(iso).slice(0, 10);
    },
  },
};
</script>

<style scoped>
.outbound-picker {
  border: 1px solid #0e6e70;
  border-radius: 5px;
  overflow: hidden;
}
.outbound-picker__hd {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  background: #e3f0ef;
  padding: 9px 12px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
}
.outbound-picker__toggle {
  color: #6c6c76;
  font-weight: 400;
}
.outbound-picker__bd {
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 9px;
}
.outbound-picker__loading {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}
.outbound-picker__msg {
  font-size: 13px;
  color: #6c6c76;
  padding: 10px 2px;
}
.outbound-picker__msg--error {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #a5431c;
}
.outbound-picker__retry {
  cursor: pointer;
  text-decoration: underline;
  color: #0e6e70;
  white-space: nowrap;
}
.outbound-picker__search {
  font-size: 13px;
}
.outbound-picker__rows {
  border: 1px solid #efede6;
  border-radius: 4px;
  max-height: 214px;
  overflow-y: auto;
}
.outbound-picker__row {
  display: flex;
  gap: 10px;
  align-items: baseline;
  padding: 8px 11px;
  border-bottom: 1px solid #efede6;
  font-size: 13px;
  cursor: pointer;
}
.outbound-picker__row:last-child {
  border-bottom: 0;
}
.outbound-picker__row:hover:not(.outbound-picker__row--off) {
  background: #f5f5f5;
}
.outbound-picker__row--off {
  color: #9b9ba3;
  cursor: not-allowed;
}
.outbound-picker__code {
  font-size: 11.5px;
  color: #6c6c76;
  min-width: 26px;
}
.outbound-picker__row--off .outbound-picker__code {
  color: #9b9ba3;
}
.outbound-picker__name {
  flex: 1;
  min-width: 0;
}
.outbound-picker__meta {
  display: block;
  font-size: 11px;
  color: #6c6c76;
  margin-top: 1px;
}
.outbound-picker__row--off .outbound-picker__meta {
  color: #9b9ba3;
}
.outbound-picker__tag {
  font-size: 10.5px;
  padding: 1px 6px;
  border-radius: 3px;
  white-space: nowrap;
  background: #efede6;
  color: #6c6c76;
  font-weight: 600;
}
.outbound-picker__ft {
  font-size: 11.5px;
  color: #6c6c76;
}
</style>
